import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/store';
import Sidebar from '../../common/sidebar-right/sidebar.component';
import InputSearch from '../../common/input-search/input-search.component';
import { ScrollableContent } from '../../common/form-field-phone/phone-code-dropdown/phone-code-dropdown.styles';
import { MenuItemsReceivers } from '../../features/parcel-creation/parcel-participant-personal/parcel-participant-personal.styles';
import MenuItem from '../../common/menu-item/menu-item.component';
import ReceiverCard from '../../features/parcel-creation/parcel-participant-personal/receiver-card/receiver-card.component';
import ReceiverCardSkeleton from '../../features/parcel-creation/parcel-participant-personal/receiver-card/receiver-card-skeleton.component';
import EmptyList from '../../common/empty-list/empty-list.component';
import { VALIDATION_RULE_LATIN } from '../../constants/validation';
import { getSettingReceiverCountry } from '../../utils/countries/countries.utils';
import { Recipient } from '../../models/parcelDeliveryMiles';
import { ReceiverContactsSkeletons } from './styles';

interface Props {
  selectedOption?: Recipient;
  onSelect: (receiver: Recipient) => Promise<void>;
}

const ParcelReceiverContactsSidebar = ({ selectedOption, onSelect }: Props) => {
  const {
    userStore: { getReceivers, receivers, isLoadingReceivers },
    parcelCreationStore: {
      shipment,
      countryDestination,
      countryDeparture,
      isDomestic,
    },
  } = useStore();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    getReceivers();
  }, [getReceivers]);

  const isDefaultFlow =
    getSettingReceiverCountry(countryDeparture?.code, isDomestic) ===
    'default_flow';
  const shouldCheckZip =
    shipment?.recipient?.carrier.delivery_service.service_type === 'courier' &&
    isDefaultFlow;

  const searchOptions = (receivers ?? [])
    .filter((receiver) => {
      const isSameCountry =
        receiver.phone?.country_code === countryDestination?.code;
      const isSameZipCode =
        receiver.address?.post_code === countryDestination?.zipCode;

      return (
        isSameCountry &&
        (!shouldCheckZip || isSameZipCode) &&
        (receiver.first_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
          receiver.last_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    })
    .sort((a, b) => a.first_name.localeCompare(b.first_name));

  return (
    <Sidebar
      name='receiver_first_name'
      withBlurredBackground
      header={t('my_receivers')}>
      {isLoadingReceivers || searchQuery || searchOptions.length > 0 ? (
        <InputSearch
          name='search_my_receivers'
          placeholder={t('search_receivers')}
          focusOnOpen
          searchIconPosition='left'
          disableSearchIconHover
          showClearInputButton
          disabled={isLoadingReceivers}
          inputValue={searchQuery}
          onChange={setSearchQuery}
          validationRule={VALIDATION_RULE_LATIN}
        />
      ) : null}
      <ScrollableContent
        aria-busy={isLoadingReceivers}
        $heightOfAboveContentMobile='19rem'
        $heightOfAboveContent='18rem'>
        {isLoadingReceivers && (
          <ReceiverContactsSkeletons aria-hidden='true'>
            {['first', 'second', 'third'].map((key) => (
              <div
                key={key}
                className='receiver-card-skeleton'>
                <ReceiverCardSkeleton />
              </div>
            ))}
          </ReceiverContactsSkeletons>
        )}
        {!isLoadingReceivers && searchOptions.length > 0 && (
          <MenuItemsReceivers>
            {searchOptions.map((receiver) => (
              <MenuItem
                key={receiver.id}
                isSelected={receiver.id === selectedOption?.id}
                onClick={() => onSelect(receiver)}>
                <ReceiverCard receiver={receiver} />
              </MenuItem>
            ))}
          </MenuItemsReceivers>
        )}
        {!isLoadingReceivers && searchOptions.length === 0 && (
          <EmptyList
            isSidebar
            description={t('please_try_again')}
            title={t('no_search_results')}
          />
        )}
      </ScrollableContent>
    </Sidebar>
  );
};

export default observer(ParcelReceiverContactsSidebar);
